import {
  DescriptionList,
  DescriptionListItem,
} from "@/components/DescriptionList";
import { TeamName } from "@/components/TeamName";
import { useTags } from "@/features/modals/TagsModal/tags";
import { getTagTranslation } from "@/features/modals/TagsModal/utils";
import { useModals } from "@/features/modals/hooks";
import styled from "@emotion/styled";
import {
  type Opportunity,
  isTeamSelection,
  updateProject,
  useTeam,
} from "@repo/api";
import {
  ListView,
  ListViewItem,
  Variables,
  locationPinSmallIcon,
  mapIcon,
  personIcon,
  tagIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  LocalizedDateAndTime,
  SectionContainer,
  translateOpportunityType,
  translateProjectSource,
  useOrderedCustomerMeetingPoints,
  useOrderedDepartments,
  useSettings,
  useToast,
} from "@repo/ui";
import { getUserInfoFromEmail, trackAnalytics } from "@repo/utils";

interface ProjectDetailsProps {
  opportunity: Opportunity;
  editable?: boolean;
  openCmpModal?: () => void;
  openHfbModal?: () => void;
  openTagModal?: () => void;
}

const TwoColumnList = styled(DescriptionList)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Variables.space75};

  @media (min-width: ${Variables.breakpointL}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: ${Variables.breakpointM}) {
    gap: ${Variables.space150};
  }
`;

const formatSourceText = (system: string, buName?: string, buCode?: string) => {
  if (buCode === "CSC") {
    return buName;
  }
  if (system === "SLA") {
    return (buName ? `${buName}` : "") + (buCode ? ` - ${buCode}` : "");
  }
  return (
    system + (buName ? ` - ${buName}` : "") + (buCode ? ` - ${buCode}` : "")
  );
};

export const ProjectDetails = ({
  editable,
  opportunity,
  openCmpModal,
  openHfbModal,
  openTagModal,
}: ProjectDetailsProps) => {
  const { updateToast } = useToast();
  const { pushModal } = useModals();
  const departments = useOrderedDepartments();
  const { data: customerMeetingPoints } = useOrderedCustomerMeetingPoints();
  const { data: tags } = useTags();
  const { market } = useSettings();
  const { data: team } = useTeam(opportunity.assignedTeam);
  const usersList = opportunity.owner
    ? [getUserInfoFromEmail(opportunity.owner)]
    : undefined;

  return (
    <SectionContainer>
      <ListView id="team-list" showDivider={editable}>
        <ListViewItem
          title={t("label.owner")}
          onClick={() => {
            if (editable) {
              pushModal({
                type: "SELECT_TEAM_OR_COWORKER",
                selectionMode: {
                  coworkerSelectionMode: "SINGLE",
                  selectOnlyTeam: true,
                },
                selected: team
                  ? {
                      kind: "team",
                      team,
                      assignedToTeam: true,
                      members: (team.teamMembers ?? []).map((m) => ({
                        ...m,
                        selected: true,
                      })),
                    }
                  : usersList,
                searchContext: "PROJECT",
                setSelected: (selected) => {
                  if (isTeamSelection(selected)) {
                    updateProject(
                      {
                        id: opportunity.id,
                        assignedTeam: selected.team.teamId,
                        owner: undefined,
                      },
                      updateToast
                    );
                  } else {
                    updateProject(
                      {
                        id: opportunity.id,
                        owner: selected?.[0]?.emailId,
                        assignedTeam: undefined,
                      },
                      updateToast
                    );
                  }
                },
              });
              trackAnalytics("owner");
            }
          }}
          description={
            opportunity.owner ? (
              <CoWorkerName email={opportunity.owner} />
            ) : (
              <TeamName teamId={opportunity.assignedTeam} />
            )
          }
          leadingIcon={personIcon}
          control={editable ? "navigational" : "default"}
        />
        <ListViewItem
          title={t("label.cmp")}
          description={
            customerMeetingPoints?.find(
              (k) => k[0] === opportunity.customerMeetingPointBuCode
            )?.[1] ?? "-"
          }
          leadingIcon={locationPinSmallIcon}
          onClick={() => {
            if (editable) {
              openCmpModal?.();
            }
            trackAnalytics("open_customer_meeting_point_modal");
          }}
          control={editable ? "navigational" : "default"}
        />
        <ListViewItem
          title={t("label.department")}
          onClick={() => {
            if (editable) {
              openHfbModal?.();
            }
            trackAnalytics("department");
          }}
          description={
            opportunity.hfb
              ?.map((h) => departments?.find((k) => k[0] === h)?.[1])
              .join(", ") ?? "-"
          }
          leadingIcon={mapIcon}
          control={editable ? "navigational" : "default"}
        />
        <ListViewItem
          title={t("label.tags")}
          onClick={() => {
            if (editable) {
              openTagModal?.();
            }
            trackAnalytics("tag");
          }}
          description={getTagTranslation(opportunity?.tags, tags, market)}
          leadingIcon={tagIcon}
          control={editable ? "navigational" : "default"}
        />
      </ListView>

      <TwoColumnList>
        <DescriptionListItem
          title={t("label.id")}
          description={opportunity.id?.toString()}
        />
        <DescriptionListItem
          title={t("label.type")}
          description={translateOpportunityType(opportunity.type)}
        />
        <DescriptionListItem
          title={t("label.source")}
          description={formatSourceText(
            translateProjectSource(opportunity.source),
            opportunity.businessUnit.buName,
            opportunity.businessUnit.buCode
          )}
        />
        <DescriptionListItem
          title={t("label.created")}
          description={<LocalizedDateAndTime date={opportunity.createdAt} />}
        />
        <DescriptionListItem
          title={t("label.updated")}
          description={
            opportunity.updatedAt ? (
              <LocalizedDateAndTime date={opportunity.updatedAt} />
            ) : (
              "-"
            )
          }
        />
        <DescriptionListItem
          title={t("label.country")}
          description={opportunity.country}
        />
      </TwoColumnList>
    </SectionContainer>
  );
};
